import React, { useMemo, useState, useEffect } from "react";
import { GoogleMap, OverlayView, useJsApiLoader } from "@react-google-maps/api";
import { BrandclubStoreConfigUtils } from "@brandclub/common-ui";
import {
  styled,
  Box,
  alpha,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { getRetailerFromId } from "@brandclub/common-ui";
import Loading from "@/components/Loading";

// Inform TypeScript about the global Google Maps object injected by the Maps JS SDK
declare const google: any;

const defaultCenter = {
  lat: 47.6194686,
  lng: -122.3190887,
}; // Seattle

// Offset a lat/lng by meters north/east
const offsetLatLngByMeters = (
  pos: { lat: number; lng: number },
  northMeters: number,
  eastMeters: number
) => {
  const earthRadiusMeters = 6378137; // WGS84
  const dLat = northMeters / earthRadiusMeters;
  const dLng =
    eastMeters / (earthRadiusMeters * Math.cos((Math.PI * pos.lat) / 180));
  return {
    lat: pos.lat + (dLat * 180) / Math.PI,
    lng: pos.lng + (dLng * 180) / Math.PI,
  };
};

const CustomPinContainer = styled(Box)(({ theme }) => ({
  pointerEvents: "none",
  position: "relative",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: 999,
  backgroundColor: theme.vars.palette.text.primary,
  width: 35,
  height: 35,
  svg: {
    position: "relative",
    zIndex: 1,
  },
  ":before": {
    animation: `shadow-pulse__RutlBg2 2.5s infinite cubic-bezier(0.25, 1, 0.5, 1)`,
    backgroundColor: theme.palette.text.primary,
    borderRadius: 999,
    content: '""',
    display: "block",
    height: `35px`,
    width: `35px`,
    position: "absolute",
    right: `0px`,
    top: `0px`,

    "@keyframes  shadow-pulse__RutlBg2": {
      "0%": {
        boxShadow: `0 0 0 0px ${alpha(theme.palette.text.primary, 0.5)}`,
      },

      "100%": { boxShadow: `0 0 0 ${26}px transparent` },
    },
  },
  ":after": {
    animation: `shadow-pulse__RutlBg 2.5s infinite cubic-bezier(0.25, 1, 0.5, 1)`,
    backgroundColor: theme.palette.text.primary,
    borderRadius: 999,
    content: '""',
    display: "block",
    height: `35px`,
    width: `35px`,
    position: "absolute",
    right: `0px`,
    top: `0px`,

    "@keyframes  shadow-pulse__RutlBg": {
      "0%": { boxShadow: `0 0 0 0 ${alpha(theme.palette.text.primary, 0.7)}` },
      "100%": { boxShadow: `0 0 0 ${12}px transparent` },
    },
  },
}));

// Simple custom pin
const CustomPin = () => (
  <CustomPinContainer>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18.496"
      height="19.344"
      viewBox="0 0 18.496 19.344"
      style={{}}
    >
      <g data-name="box (9)">
        <path
          data-name="Path 26301"
          d="m29.387 5.144 8.513-3.65L34.376.046a.612.612 0 0 0-.465 0l-8.467 3.478z"
          transform="translate(-24.895)"
          style={{ fill: "#fff" }}
        />
        <path
          data-name="Path 26302"
          d="m178.529 56.125-8.516 3.649 3.174 1.3 8.7-3.574z"
          transform="translate(-163.939 -53.98)"
          style={{ fill: "#fff" }}
        />
        <path
          data-name="Path 26303"
          d="m19.718 124.456-3.445-1.415v2.68a.611.611 0 1 1-1.223 0v-3.182l-3.969-1.63v10.765a.612.612 0 0 0 .379.566l8.258 3.392z"
          transform="translate(-11.081 -116.287)"
          style={{ fill: "#fff" }}
        />
        <path
          data-name="Path 26304"
          d="M269.069 124.456v11.175l8.258-3.392a.612.612 0 0 0 .379-.566v-10.765z"
          transform="translate(-259.209 -116.287)"
          style={{ fill: "#fff" }}
        />
      </g>
    </svg>
  </CustomPinContainer>
);

const mapStyles = [
  {
    featureType: "administrative",
    elementType: "all",
    stylers: [
      {
        saturation: "-100",
      },
    ],
  },
  {
    featureType: "administrative.province",
    elementType: "all",
    stylers: [
      {
        visibility: "off",
      },
    ],
  },
  {
    featureType: "landscape",
    elementType: "all",
    stylers: [
      {
        saturation: -100,
      },
      {
        lightness: 65,
      },
      {
        visibility: "on",
      },
    ],
  },
  {
    featureType: "poi",
    elementType: "all",
    stylers: [
      {
        saturation: -100,
      },
      {
        lightness: "50",
      },
      {
        visibility: "simplified",
      },
    ],
  },
  {
    featureType: "road",
    elementType: "all",
    stylers: [
      {
        saturation: "-100",
      },
    ],
  },
  {
    featureType: "road.highway",
    elementType: "all",
    stylers: [
      {
        visibility: "simplified",
      },
    ],
  },
  {
    featureType: "road.arterial",
    elementType: "all",
    stylers: [
      {
        lightness: "30",
      },
    ],
  },
  {
    featureType: "road.local",
    elementType: "all",
    stylers: [
      {
        lightness: "40",
      },
    ],
  },
  {
    featureType: "transit",
    elementType: "all",
    stylers: [
      {
        saturation: -100,
      },
      {
        visibility: "simplified",
      },
    ],
  },
  {
    featureType: "water",
    elementType: "geometry",
    stylers: [
      {
        hue: "#ffff00",
      },
      {
        lightness: -25,
      },
      {
        saturation: -97,
      },
    ],
  },
  {
    featureType: "water",
    elementType: "labels",
    stylers: [
      {
        lightness: -25,
      },
      {
        saturation: -100,
      },
    ],
  },
  // Hide parcel and landscape labels (removes house/unit numbers inside blocks)
  {
    featureType: "administrative.land_parcel",
    elementType: "labels",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "landscape",
    elementType: "labels",
    stylers: [{ visibility: "off" }],
  },
  // Reduce street name density: hide local and arterial road labels; simplify highways
  {
    featureType: "road.local",
    elementType: "labels",
    stylers: [{ visibility: "on" }],
  },
  {
    featureType: "road.arterial",
    elementType: "labels",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "road.highway",
    elementType: "labels",
    stylers: [{ visibility: "simplified" }],
  },
  // Reduce POI label density: hide most, selectively allow a few categories
  {
    featureType: "poi",
    elementType: "labels",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "poi.business",
    elementType: "labels",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "poi.place_of_worship",
    elementType: "labels",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "poi.sports_complex",
    elementType: "labels",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "poi.attraction",
    elementType: "labels",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "poi.park",
    elementType: "labels",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "poi.government",
    elementType: "labels",
    stylers: [{ visibility: "off" }],
  },
  // Explicitly hide schools and medical labels to reduce density further
  {
    featureType: "poi.school",
    elementType: "labels",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "poi.medical",
    elementType: "labels",
    stylers: [{ visibility: "off" }],
  },
  // Hide transit station labels
  {
    featureType: "transit",
    elementType: "labels",
    stylers: [{ visibility: "off" }],
  },
];

const GeoConfettiContainer = styled(Box)({
  position: "relative",
  width: "100%",
  height: "100%",
  overflow: "hidden",

  ".geo-confetti-map": {
    height: "100%",
    width: "100%",
  },
});

const GeoConfettiInfoContainer = styled(Box)(({ theme }) => ({
  position: "absolute",
  bottom: 20,
  left: 0,
  right: 0,
  display: "flex",
  alignItems: "flex-end",
  justifyContent: "center",
  pointerEvents: "none",
  [theme.breakpoints.down("md")]: {
    bottom: 0,
  },
}));

const InfoWrap = styled(Box)(({ theme }) => ({
  position: "relative",
  width: "calc(100% - 40px)",
  maxWidth: 775,
  display: "flex",
  justifyContent: "center",
  [theme.breakpoints.down("md")]: {
    width: "100%",
    maxWidth: "100%",
  },
}));

const InfoCardShadowWrap = styled(Box)(({ theme }) => ({
  position: "relative",
  width: "100%",
  filter: "drop-shadow(0px 10px 20px rgba(0,0,0,0.15))",
  borderTopLeftRadius: 24,
  borderTopRightRadius: 24,
  borderBottomLeftRadius: 24,
  borderBottomRightRadius: 24,
  [theme.breakpoints.down("md")]: {
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    borderBottomLeftRadius: 24,
    borderBottomRightRadius: 24,
  },
}));

const InfoCard = styled(Box)(({ theme }) => ({
  position: "relative",
  backgroundColor: theme.vars.palette.background.paper,
  padding: "70px 32px 36px",
  width: "100%",
  textAlign: "center",
  pointerEvents: "auto",
  boxSizing: "border-box",
  borderTopLeftRadius: 24,
  borderTopRightRadius: 24,
  borderBottomLeftRadius: 24,
  borderBottomRightRadius: 24,

  ".overline": {
    display: "block",
    fontSize: 16,
    fontWeight: 600,
    color: theme.vars.palette.text.primary,
    marginBottom: 8,
  },
  ".title": {
    fontSize: 24,
    lineHeight: 1.2,
    fontWeight: 700,
    color: theme.vars.palette.text.primary,
    marginBottom: 12,
  },
  ".subtitle": {
    fontSize: 16,
    lineHeight: 1.55,
    fontWeight: 500,
    color: theme.vars.palette.text.primary,
    maxWidth: 520,
    margin: "0 auto",
  },
  [theme.breakpoints.down("md")]: {
    padding: "54px 27px 28px",
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    ".overline": {
      fontSize: 12,
      marginBottom: 4,
    },
    ".title": { fontSize: 20 },
    ".subtitle": { fontSize: 14 },
  },
}));

const LoaderBadge = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: -57,
  left: 0,
  right: 0,
  margin: "0 auto",
  width: 118,
  height: 118,
  borderRadius: 999,
  backgroundColor: theme.vars.palette.background.paper,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: "none",
  [theme.breakpoints.down("md")]: {
    top: -45,
    width: 90,
    height: 90,
  },
}));

const getRetailerIdFromOrderId = (orderId: string) => {
  const retailerId = orderId.split("-")[0];
  return retailerId;
};

const GeoConfettiInfo = () => {
  const theme = useTheme();
  const responsiveView = useMediaQuery(theme.breakpoints.down("md"));
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("orderId");
  const retailerName = (() => {
    if (!orderId) return "retailer";
    const retailerIdStr = getRetailerIdFromOrderId(orderId);
    const retailerId = parseInt(retailerIdStr || "", 10);
    if (Number.isFinite(retailerId)) {
      return getRetailerFromId(retailerId);
    }
    return "retailer";
  })();

  return (
    <GeoConfettiInfoContainer className="geo-confetti-info">
      <InfoWrap>
        <InfoCardShadowWrap className="info-card-shadow-wrap">
          <InfoCard className="info-card">
            {orderId && (
              <Typography className="overline">{`Order #${orderId}`}</Typography>
            )}
            <Typography className="title">Thanks for your order!</Typography>
            <Typography className="subtitle">
              {`We’re placing your order with ${retailerName} — we’ll send you a text message if we need more information to complete your order.`}
            </Typography>
          </InfoCard>
          <LoaderBadge>
            <Loading
              storeSpin
              size={responsiveView ? 75 : 95}
              thickness={6}
              spinCustomColor={theme.vars.palette.text.primary}
            />
          </LoaderBadge>
        </InfoCardShadowWrap>
      </InfoWrap>
    </GeoConfettiInfoContainer>
  );
};

const GeoConfetti = ({ addressString }: { addressString?: string }) => {
  const [pinPosition, setPinPosition] =
    useState<{ lat: number; lng: number }>(defaultCenter);
  const theme = useTheme();
  const responsiveView = useMediaQuery(theme.breakpoints.down("md"));
  // Resolve key using helper (handles multiple sources)
  const apiKey = BrandclubStoreConfigUtils.getGoogleMapsApiKey();

  const { isLoaded, loadError } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: apiKey || "",
    // version: 'weekly', // optional pin to latest weekly
  });

  // Compute a slight marker offset from center
  const offsetMeters = responsiveView ? 20 : 60;
  const offsetDirection = useMemo(
    () => (Math.random() < 0.5 ? "upperLeft" : "upperRight"),
    []
  );

  // update pin position with address string using geocoder
  useEffect(() => {
    if (!addressString) return;

    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 20;
    const intervalMs = 250;

    const tryGeocode = () => {
      if (cancelled) return;
      try {
        const GeocoderCtor =
          typeof google !== "undefined" ? google?.maps?.Geocoder : undefined;

        if (!GeocoderCtor) {
          if (attempts < maxAttempts) {
            attempts += 1;
            setTimeout(tryGeocode, intervalMs);
          }
          return;
        }

        const geocoder = new GeocoderCtor();
        geocoder.geocode(
          { address: addressString },
          (results: any, status: string) => {
            if (cancelled) return;
            if (status === "OK" && results?.[0]?.geometry?.location) {
              const loc = results[0].geometry.location;
              const next = { lat: loc.lat(), lng: loc.lng() };
              setPinPosition(next);
            }
          }
        );
      } catch (e) {
        if (attempts < maxAttempts) {
          attempts += 1;
          setTimeout(tryGeocode, intervalMs);
        }
      }
    };

    tryGeocode();

    return () => {
      cancelled = true;
    };
  }, [addressString]);

  // useEffect(() => {}, [ addressString]);

  const directionToOffset = (dir: string) => {
    switch (dir) {
      case "upperLeft":
        return { north: offsetMeters, east: -offsetMeters };
      case "upperRight":
        return { north: offsetMeters, east: offsetMeters };
      case "lowerLeft":
        return { north: -offsetMeters, east: -offsetMeters };
      case "lowerRight":
        return { north: -offsetMeters, east: offsetMeters };
      default:
        return { north: 0, east: 0 };
    }
  };
  const { north, east } = directionToOffset(offsetDirection);

  const mapCenter = offsetLatLngByMeters(pinPosition, -north, -east);

  return (
    <GeoConfettiContainer>
      {/* Body */}
      <div className="geo-confetti-map">
        {apiKey && isLoaded && !loadError && (
          <GoogleMap
            mapContainerStyle={{ width: "100%", height: "100%" }}
            center={mapCenter}
            zoom={17.8}
            options={{
              disableDefaultUI: true,
              streetViewControl: false,
              mapTypeControl: false,
              fullscreenControl: false,
              zoomControl: false,
              rotateControl: false,
              scaleControl: false,
              gestureHandling: "none",
              draggable: false,
              scrollwheel: false,
              disableDoubleClickZoom: true,
              keyboardShortcuts: false,
              clickableIcons: false,
              styles: mapStyles,
            }}
          >
            <OverlayView
              position={pinPosition}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
              getPixelPositionOffset={(width, height) => ({
                x: -Math.round(width / 2),
                y: -Math.round(height / 2),
              })}
            >
              <CustomPin />
            </OverlayView>
          </GoogleMap>
        )}
      </div>
      <GeoConfettiInfo />
    </GeoConfettiContainer>
  );
};

export default GeoConfetti;
